import { useLoaderData, useSearchParams } from '@remix-run/react';
import { useEffect, useMemo, useState } from 'react';
import type { loader as rootLoader } from '~/routes/_index.tsx';
import type { UIComponent } from '~/services/layout';
import { getClientFeedUrl } from '~/services/request';
import { isValidUrl } from '~/utils/validations';
import useCurrentLanguage from './use-current-language';
import { getComputedUrl } from '~/services/pipes/utils';
import { useTabIndex } from '~/contexts/tab-index-context';
import {
  CONTEXT_SETTER_READY_EVENT,
  getStoredSessionContextKeys,
  getStoredContextSetterKeys,
  hasRemoteContextResolved,
  hasStoredContextSetterKeys,
} from '~/utils/context-setter-storage.ts';
import { uiComponentRequiresRemoteClientContext } from '~/services/plugins/zapp-context-setter/utils/index.ts';
import { isQueueActionSource } from '~/services/ui-components/is-queue-action-source.ts';

export type ClientFeedApiEndpointResult = {
  clientFeedApiEndpoint: string | null;
  hasClientContextInRequest: boolean;
  feedRequiresClientContext: boolean;
  hasContextSetterResolved: boolean;
};

export function useClientFeedApiEndpoint({
  uiComponent,
  applyClientContext = true,
}: {
  uiComponent: UIComponent;
  applyClientContext?: boolean;
}): ClientFeedApiEndpointResult {
  const {
    serverLoadedFeeds,
    baseURL,
    isTabsScreen,
    tabs,
    screenEntry,
    pathParams,
    screen,
    dynamicIdFallback,
    contextSetterRemoteUrl,
  } = useLoaderData<typeof rootLoader>();

  const currentLanguage: string = useCurrentLanguage();
  const tabIndex = useTabIndex();
  const [searchParams] = useSearchParams();
  const [clientContextVersion, setClientContextVersion] = useState<number>(0);
  const [hasContextSetterResolved, setHasContextSetterResolved] =
    useState<boolean>(
      () =>
        hasStoredContextSetterKeys() ||
        hasRemoteContextResolved(contextSetterRemoteUrl)
    );

  const q: string | null = searchParams.get('q');
  const filterTag: string | null = searchParams.get('filterTag');

  useEffect(() => {
    const syncStoredClientContext = (): void => {
      setClientContextVersion((version) => version + 1);
      setHasContextSetterResolved(true);
    };

    if (
      hasStoredContextSetterKeys() ||
      hasRemoteContextResolved(contextSetterRemoteUrl)
    ) {
      syncStoredClientContext();
    }

    window.addEventListener(
      CONTEXT_SETTER_READY_EVENT,
      syncStoredClientContext
    );

    return () => {
      window.removeEventListener(
        CONTEXT_SETTER_READY_EVENT,
        syncStoredClientContext
      );
    };
  }, [contextSetterRemoteUrl]);

  const entry =
    isTabsScreen && tabs?.entry && tabIndex !== undefined
      ? tabs.entry[tabIndex]
      : screenEntry || serverLoadedFeeds?.[0]?.feed?.entry?.[0];

  const feedUrl: string | undefined = getComputedUrl({
    component: uiComponent,
    entry,
    q,
    filterTag,
    pathParams,
    dynamicIdFallback,
    disablePathParamsFallback: Boolean(
      screen?.general?.disable_dynamic_id_fallback
    ),
  });

  const storedClientContextKeys = useMemo(() => {
    void clientContextVersion;

    return getStoredContextSetterKeys();
  }, [clientContextVersion]);

  const storedSessionContextKeys = useMemo(() => {
    void clientContextVersion;

    return getStoredSessionContextKeys(uiComponent?._endpointContextKeys || []);
  }, [clientContextVersion, uiComponent?._endpointContextKeys]);

  if (typeof feedUrl === 'string' && isQueueActionSource(feedUrl)) {
    return {
      clientFeedApiEndpoint: feedUrl,
      hasClientContextInRequest: false,
      feedRequiresClientContext: false,
      hasContextSetterResolved,
    };
  }

  if (!isValidUrl(feedUrl)) {
    return {
      clientFeedApiEndpoint: null,
      hasClientContextInRequest: false,
      feedRequiresClientContext: false,
      hasContextSetterResolved,
    };
  }

  const mergedClientContextKeys = {
    ...storedSessionContextKeys,
    ...storedClientContextKeys,
  };

  const hasClientContextInRequest: boolean =
    applyClientContext && Object.keys(mergedClientContextKeys).length > 0;

  const feedRequiresClientContextValue: boolean =
    Boolean(contextSetterRemoteUrl) &&
    uiComponentRequiresRemoteClientContext(uiComponent);

  const clientFeedApiEndpoint: string = addQueryParam({
    clientFeedApiEndpoint: `${baseURL}/${getClientFeedUrl(
      feedUrl,
      hasClientContextInRequest ? mergedClientContextKeys : undefined
    )}`,
    key: 'language',
    value: currentLanguage,
  });

  return {
    clientFeedApiEndpoint: clientFeedApiEndpoint || null,
    hasClientContextInRequest,
    feedRequiresClientContext: feedRequiresClientContextValue,
    hasContextSetterResolved,
  };
}

function addQueryParam({
  clientFeedApiEndpoint,
  key,
  value,
}: {
  clientFeedApiEndpoint: string;
  key: string;
  value: string;
}): string {
  try {
    const clientFeedApiEndpointURL = new URL(clientFeedApiEndpoint);
    clientFeedApiEndpointURL.searchParams.set(key, value);
    return `${clientFeedApiEndpointURL.pathname}${clientFeedApiEndpointURL.search}`;
  } catch (error) {
    return clientFeedApiEndpoint;
  }
}
